import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '../ui/SearchableSelect';
import { cn } from '../../utils/cn';
import {
  INCIDENT_CATEGORIES,
  INCIDENT_TYPES_BY_CATEGORY,
  INCIDENT_TYPE_LABEL,
} from '../../constants/incidents';
import { MAX_RECENT_INCIDENT_TYPES } from './useRecentIncidentTypes';

/**
 * Texto comparable para la búsqueda: sin tildes ni diéresis (NFD + quitar las
 * marcas combinantes), en minúsculas y con los espacios colapsados. Así
 * "perdida" encuentra "Pérdida" y "senal" encuentra "señal".
 * @param {unknown} text
 * @returns {string}
 */
export function normalizeSearchText(text) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * filterOption de react-select: cada palabra escrita tiene que aparecer en la
 * etiqueta del tipo o en la de su categoría, sin importar tildes ni el orden
 * ("pérdida" devuelve los tipos de esa categoría; "roto paquete", "Paquete
 * roto"). No se usa el filtro por defecto de react-select porque distingue
 * tildes.
 * @param {{ data?: { label?: string, category?: string } }} option
 * @param {string} inputValue
 * @returns {boolean}
 */
export function incidentTypeFilterOption(option, inputValue) {
  const query = normalizeSearchText(inputValue);
  if (!query) return true;
  const haystack = normalizeSearchText(`${option?.data?.label ?? ''} ${option?.data?.category ?? ''}`);
  return query.split(' ').every((word) => haystack.includes(word));
}

/**
 * Tipos que se pueden elegir al crear, en el orden del catálogo: los del
 * catálogo vigente (sin los legacy) menos los excluidos.
 * @param {string[]} [excludeTypes]
 * @returns {string[]}
 */
export function selectableIncidentTypes(excludeTypes = []) {
  return Object.values(INCIDENT_TYPES_BY_CATEGORY)
    .flatMap((types) => Object.keys(types))
    .filter((type) => !excludeTypes.includes(type));
}

/**
 * Enter en el combobox: react-select elige la opción enfocada y cancela el
 * evento, pero con el menú cerrado, o abierto sin opción enfocada ("Sin
 * resultados"), lo deja pasar y el navegador enviaría el formulario con el tipo
 * elegido antes (envío implícito). Se corta en burbuja, cuando react-select ya
 * decidió. No se toca el Enter que confirma una composición del IME.
 */
function preventImplicitSubmit(event) {
  if (event.key !== 'Enter' || event.defaultPrevented || event.nativeEvent.isComposing) return;
  event.preventDefault();
}

/** Opciones agrupadas por categoría; una categoría sin tipos elegibles no aparece. */
function buildTypeGroups(excludeTypes, t) {
  return Object.entries(INCIDENT_TYPES_BY_CATEGORY)
    .map(([categoryKey, types]) => {
      const category = t(INCIDENT_CATEGORIES[categoryKey] || categoryKey);
      const options = Object.entries(types)
        .filter(([type]) => !excludeTypes.includes(type))
        .map(([type, label]) => ({ value: type, label: t(label), category }));
      return { label: category, options };
    })
    .filter((group) => group.options.length > 0);
}

/**
 * Selector del tipo de incidencia del formulario de la mesa de incidencias:
 * chips con los tipos recientes (el caso común se resuelve con un toque, sin
 * abrir el teclado en móvil) y un select con búsqueda agrupado por categoría.
 *
 * Es controlado: el valor y los recientes los maneja el formulario, que es
 * quien sabe cuándo una incidencia se creó con éxito.
 *
 * @param {object} props
 * @param {string} props.value - tipo elegido ('' sin elección)
 * @param {(type: string) => void} props.onChange
 * @param {string[]} [props.excludeTypes] - tipos que no se ofrecen
 * @param {string[]} [props.recentTypes] - del más reciente al más antiguo
 * @param {string} props.inputId - id del input del select (el formulario lo
 *   enfoca si falta el tipo)
 * @param {string|null} [props.error] - texto del error de validación
 * @param {string} props.errorId - id del texto del error
 * @param {boolean} [props.disabled]
 */
export default function IncidentTypePicker({
  value,
  onChange,
  excludeTypes = [],
  recentTypes = [],
  inputId,
  error = null,
  errorId,
  disabled = false,
}) {
  const { t } = useTranslation();
  const recentLabelId = useId();

  const selectable = selectableIncidentTypes(excludeTypes);
  const recentChips = recentTypes
    .filter((type) => selectable.includes(type))
    .slice(0, MAX_RECENT_INCIDENT_TYPES);

  return (
    <div className="flex flex-col gap-2">
      {recentChips.length > 0 && (
        <div role="group" aria-labelledby={recentLabelId} className="flex flex-wrap items-center gap-2">
          <span id={recentLabelId} className="text-xs font-medium text-fg-muted">
            {t('Recientes')}
          </span>
          {recentChips.map((type) => {
            const isCurrent = value === type;
            return (
              <button
                key={type}
                type="button"
                aria-pressed={isCurrent}
                disabled={disabled}
                onClick={() => {
                  if (!isCurrent) onChange(type);
                }}
                className={cn(
                  'inline-flex h-11 items-center rounded-full border px-3 text-sm transition-colors md:h-8',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  isCurrent
                    ? 'border-brand-700 bg-brand-50 font-medium text-brand-700'
                    : 'border-border bg-bg text-fg hover:bg-bg-muted',
                )}
              >
                {t(INCIDENT_TYPE_LABEL[type])}
              </button>
            );
          })}
        </div>
      )}

      {/* El único foco posible acá adentro es el input del combobox (no es
          clearable y las opciones usan foco virtual). */}
      <div onKeyDown={preventImplicitSubmit}>
        <SearchableSelect
          id={inputId}
          label={
            <>
              {t('Tipo de incidencia')}{' '}
              <span aria-hidden="true" className="text-status-error">
                *
              </span>
            </>
          }
          options={buildTypeGroups(excludeTypes, t)}
          value={value}
          // El select no se puede vaciar (no es clearable): siempre llega un tipo.
          onChange={(next) => {
            if (next) onChange(next);
          }}
          placeholder={t('Elige el tipo')}
          filterOption={incidentTypeFilterOption}
          isDisabled={disabled}
          // `required` le da aria-required al combobox; el formulario es
          // noValidate y valida por su cuenta.
          required
          aria-invalid={error ? true : undefined}
          aria-errormessage={error ? errorId : undefined}
          // 16 px en móvil: con menos, iOS hace zoom al enfocar el campo.
          controlClassName={cn('text-base md:text-sm', error && 'border-status-error')}
        />
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-xs text-status-error">
          {error}
        </p>
      )}
    </div>
  );
}
